import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';

interface CodeLine {
  text: string;
  className?: string;
  delay?: number;
}

interface CodeDemoProps {
  demoType: 'agent' | 'autocomplete' | 'codebase' | 'testing' | 'terminal' | 'git' | 'debug';
}

const demos: Record<string, { lines: CodeLine[]; aiSuggestion?: string; fileName: string }> = {
  agent: {
    fileName: 'refactor-task.ts',
    lines: [
      { text: '// Agent: "Refactor this to use async/await and add tests"', className: 'text-text-tertiary italic' },
      { text: '', delay: 300 },
      { text: '// Refactoring api/users.ts...', className: 'text-accent', delay: 200 },
      { text: 'export async function getUsers(): Promise<User[]> {', className: 'text-purple-400', delay: 80 },
      { text: '  const response = await fetch("/api/users");', className: 'text-blue-400', delay: 60 },
      { text: '  return response.json();', className: 'text-green-400', delay: 60 },
      { text: '}', className: 'text-purple-400', delay: 50 },
      { text: '', delay: 100 },
      { text: '// Generating tests/users.test.ts...', className: 'text-accent', delay: 200 },
      { text: 'describe("getUsers", () => {', className: 'text-purple-400', delay: 80 },
      { text: '  it("should return user list", async () => {', className: 'text-yellow-400', delay: 60 },
      { text: '    const users = await getUsers();', className: 'text-blue-400', delay: 60 },
      { text: '    expect(users).toBeInstanceOf(Array);', className: 'text-green-400', delay: 60 },
      { text: '  });', className: 'text-yellow-400', delay: 50 },
      { text: '});', className: 'text-purple-400', delay: 50 },
    ],
    aiSuggestion: 'Refactored 2 files, generated 3 test cases',
  },
  autocomplete: {
    fileName: 'PaymentForm.tsx',
    lines: [
      { text: 'import { useState } from "react";', className: 'text-purple-400' },
      { text: 'import { validateCard } from "./utils";', className: 'text-purple-400', delay: 40 },
      { text: '', delay: 150 },
      { text: 'function PaymentForm() {', className: 'text-purple-400', delay: 60 },
      { text: '  const [card, setCard] = useState("");', className: 'text-blue-400', delay: 40 },
      { text: '  // Start typing...', className: 'text-text-tertiary', delay: 100 },
      { text: '  const handleSubmit = async (e) => {', className: 'text-purple-400', delay: 40 },
      { text: '    e.preventDefault();', className: 'text-blue-400', delay: 30 },
      { text: '    if (validateCard(card)) {', className: 'text-blue-400', delay: 30 },
      { text: '      await processPayment(card);', className: 'text-green-400', delay: 30 },
      { text: '      showSuccess("Payment complete!");', className: 'text-green-400', delay: 30 },
      { text: '    }', className: 'text-blue-400', delay: 20 },
      { text: '  };', className: 'text-purple-400', delay: 20 },
      { text: '}', className: 'text-purple-400', delay: 20 },
    ],
    aiSuggestion: 'Multi-line completion accepted with Tab',
  },
  codebase: {
    fileName: 'Search: "payment processing"',
    lines: [
      { text: '// Semantic search across codebase...', className: 'text-text-tertiary italic' },
      { text: '', delay: 300 },
      { text: '📁 src/services/payment.ts', className: 'text-accent', delay: 150 },
      { text: '   export function processPayment(amount: number)', className: 'text-blue-400', delay: 60 },
      { text: '   export function refundPayment(transactionId: string)', className: 'text-blue-400', delay: 60 },
      { text: '', delay: 100 },
      { text: '📁 src/api/stripe.ts', className: 'text-accent', delay: 150 },
      { text: '   async function createCharge(customer, amount)', className: 'text-blue-400', delay: 60 },
      { text: '   async function createSubscription(plan)', className: 'text-blue-400', delay: 60 },
      { text: '', delay: 100 },
      { text: '📁 src/hooks/usePayment.ts', className: 'text-accent', delay: 150 },
      { text: '   export function usePayment(): PaymentHook', className: 'text-blue-400', delay: 60 },
      { text: '', delay: 100 },
      { text: '// 12 symbols found in 5 files', className: 'text-green-400', delay: 200 },
    ],
    aiSuggestion: 'Click any result to navigate instantly',
  },
  testing: {
    fileName: 'user.test.ts',
    lines: [
      { text: '// Running Unit Tests...', className: 'text-text-tertiary italic' },
      { text: '', delay: 200 },
      { text: '✓ should create new user', className: 'text-green-400', delay: 100 },
      { text: '✓ should validate email format', className: 'text-green-400', delay: 80 },
      { text: '✓ should hash password correctly', className: 'text-green-400', delay: 80 },
      { text: '✗ should reject duplicate emails', className: 'text-red-400', delay: 100 },
      { text: '  → Expected: Error("Email exists")', className: 'text-text-tertiary', delay: 50 },
      { text: '  → Received: undefined', className: 'text-red-400', delay: 50 },
      { text: '', delay: 150 },
      { text: '// AI Fix Suggestion:', className: 'text-accent', delay: 200 },
      { text: 'if (await User.findByEmail(email)) {', className: 'text-purple-400', delay: 60 },
      { text: '  throw new Error("Email exists");', className: 'text-blue-400', delay: 60 },
      { text: '}', className: 'text-purple-400', delay: 40 },
      { text: '', delay: 100 },
      { text: '📊 Coverage: 94% | Tests: 23/24 passed', className: 'text-green-400', delay: 150 },
    ],
    aiSuggestion: 'AI detected issue and suggested fix',
  },
  terminal: {
    fileName: 'Terminal',
    lines: [
      { text: '$ npm run build', className: 'text-green-400' },
      { text: '', delay: 200 },
      { text: '> building for production...', className: 'text-text-tertiary', delay: 100 },
      { text: '✓ 142 modules transformed', className: 'text-blue-400', delay: 80 },
      { text: '✗ Error: Cannot find module "lodash"', className: 'text-red-400', delay: 150 },
      { text: '', delay: 100 },
      { text: '// AI Assistant:', className: 'text-accent', delay: 200 },
      { text: 'Missing dependency detected. Run:', className: 'text-text-secondary', delay: 80 },
      { text: '', delay: 50 },
      { text: '$ npm install lodash', className: 'text-green-400', delay: 100 },
      { text: '', delay: 150 },
      { text: '+ lodash@4.17.21', className: 'text-blue-400', delay: 80 },
      { text: 'added 1 package in 2.1s', className: 'text-text-tertiary', delay: 80 },
      { text: '', delay: 100 },
      { text: '✓ Build successful!', className: 'text-green-400', delay: 150 },
    ],
    aiSuggestion: 'AI auto-fixed missing dependency',
  },
  git: {
    fileName: 'Source Control',
    lines: [
      { text: '// Generating commit message...', className: 'text-text-tertiary italic' },
      { text: '', delay: 300 },
      { text: 'Changes staged: 5 files', className: 'text-accent', delay: 100 },
      { text: '  M  src/auth/login.ts', className: 'text-yellow-400', delay: 60 },
      { text: '  M  src/auth/register.ts', className: 'text-yellow-400', delay: 60 },
      { text: '  A  src/auth/oauth.ts', className: 'text-green-400', delay: 60 },
      { text: '  M  src/api/routes.ts', className: 'text-yellow-400', delay: 60 },
      { text: '  D  src/auth/legacy.ts', className: 'text-red-400', delay: 60 },
      { text: '', delay: 200 },
      { text: '// AI Generated Commit:', className: 'text-accent', delay: 150 },
      { text: 'feat(auth): Add OAuth 2.0 support', className: 'text-purple-400', delay: 80 },
      { text: '', delay: 50 },
      { text: '- Add Google and GitHub OAuth providers', className: 'text-text-secondary', delay: 60 },
      { text: '- Remove deprecated legacy auth system', className: 'text-text-secondary', delay: 60 },
      { text: '- Update API routes for new flow', className: 'text-text-secondary', delay: 60 },
    ],
    aiSuggestion: 'Press Enter to commit with AI message',
  },
  debug: {
    fileName: 'Debugger',
    lines: [
      { text: '// Breakpoint hit at line 42', className: 'text-text-tertiary italic' },
      { text: '', delay: 200 },
      { text: '→ calculateTotal(items: CartItem[])', className: 'text-red-400', delay: 100 },
      { text: '', delay: 100 },
      { text: 'Variables:', className: 'text-accent', delay: 150 },
      { text: '  items: Array(3)', className: 'text-blue-400', delay: 60 },
      { text: '  total: NaN', className: 'text-red-400', delay: 60 },
      { text: '  discount: undefined', className: 'text-yellow-400', delay: 60 },
      { text: '', delay: 200 },
      { text: '// AI Analysis:', className: 'text-accent', delay: 150 },
      { text: '"discount" is undefined, causing NaN.', className: 'text-text-secondary', delay: 80 },
      { text: 'Suggested fix:', className: 'text-text-secondary', delay: 80 },
      { text: '', delay: 50 },
      { text: 'const discount = options?.discount ?? 0;', className: 'text-green-400', delay: 80 },
      { text: '', delay: 100 },
      { text: '✓ Issue resolved, continuing...', className: 'text-green-400', delay: 150 },
    ],
    aiSuggestion: 'AI identified root cause and fixed it',
  },
};

export function CodeDemo({ demoType }: CodeDemoProps) {
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [currentCharIndex, setCurrentCharIndex] = useState<number>(0);
  const [isTyping, setIsTyping] = useState(true);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const demo = demos[demoType];
  const lines = demo.lines;

  useEffect(() => {
    setVisibleLines(0);
    setCurrentCharIndex(0);
    setIsTyping(true);
    setShowSuggestion(false);
  }, [demoType]);

  useEffect(() => {
    if (!isTyping) return;

    const currentLine = lines[visibleLines];
    if (!currentLine) {
      setIsTyping(false);
      setTimeout(() => setShowSuggestion(true), 500);
      return;
    }

    const baseDelay = currentLine.delay || 40;
    const charDelay = baseDelay / 2;

    if (currentCharIndex < currentLine.text.length) {
      const timer = setTimeout(() => {
        setCurrentCharIndex(prev => prev + 1);
      }, charDelay);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setVisibleLines(prev => prev + 1);
        setCurrentCharIndex(0);
      }, baseDelay);
      return () => clearTimeout(timer);
    }
  }, [visibleLines, currentCharIndex, isTyping, lines]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [visibleLines, currentCharIndex]);

  return (
    <div className="relative h-full">
      {/* Editor Header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-bg-elevated/50 border-b border-border-default">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <span className="text-xs text-text-tertiary ml-2">{demo.fileName}</span>
      </div>

      {/* Code Area */}
      <div
        ref={containerRef}
        className="p-4 font-mono text-sm overflow-auto"
        style={{ height: 'calc(100% - 80px)' }}
      >
        {lines.slice(0, visibleLines + 1).map((line, index) => {
          const isCurrentLine = index === visibleLines;
          const displayText = isCurrentLine
            ? line.text.slice(0, currentCharIndex)
            : line.text;

          return (
            <div key={index} className="flex">
              <span className="w-8 text-text-tertiary/50 select-none text-right pr-4">
                {index + 1}
              </span>
              <pre className={cn('flex-1', line.className || 'text-text-primary')}>
                {displayText}
                {isCurrentLine && isTyping && (
                  <span className="inline-block w-2 h-4 bg-accent ml-0.5 animate-pulse" />
                )}
              </pre>
            </div>
          );
        })}
      </div>

      {/* AI Suggestion Badge */}
      {showSuggestion && demo.aiSuggestion && (
        <div className="absolute bottom-4 left-4 right-4 animate-fade-in">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/30 text-accent text-xs">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            {demo.aiSuggestion}
          </div>
        </div>
      )}
    </div>
  );
}
